// Minimal hyperscript helper. Not a framework: just enough to build views as
// typed element trees instead of innerHTML strings, so event wiring is direct
// and there is no markup-injection surface for user text.

type Child = Node | string | null | undefined | false;

interface Props {
  class?: string;
  text?: string;
  html?: never; // intentionally unavailable: never set innerHTML from data
  value?: string;
  type?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  step?: string;
  disabled?: boolean;
  href?: string;
  title?: string;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
  on?: Partial<{
    [K in keyof HTMLElementEventMap]: (e: HTMLElementEventMap[K]) => void;
  }>;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.class) node.className = props.class;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.title) node.title = props.title;
  if (props.value !== undefined) (node as HTMLInputElement).value = props.value;
  if (props.type) (node as HTMLInputElement).type = props.type;
  if (props.placeholder)
    (node as HTMLInputElement).placeholder = props.placeholder;
  if (props.min !== undefined) (node as HTMLInputElement).min = props.min;
  if (props.max !== undefined) (node as HTMLInputElement).max = props.max;
  if (props.step !== undefined) (node as HTMLInputElement).step = props.step;
  if (props.disabled) (node as HTMLButtonElement).disabled = true;
  if (props.href) (node as HTMLAnchorElement).href = props.href;
  if (props.dataset)
    for (const [k, v] of Object.entries(props.dataset)) node.dataset[k] = v;
  if (props.attrs)
    for (const [k, v] of Object.entries(props.attrs)) node.setAttribute(k, v);
  if (props.on)
    for (const [type, handler] of Object.entries(props.on))
      node.addEventListener(type, handler as EventListener);
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child);
  }
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function pct(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}
